import { useEffect } from 'react'
import axios from 'axios'
import { useAuthStore } from '@/store/authStore'

// API base URL — switch between dev and prod
export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

// ── Axios Instance ─────────────────────────────────────────────────────────
// Centralized HTTP client with auth interceptor + auto token refresh
// ───────────────────────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // ← CORS FIX: Add this to send cookies/auth headers cross-origin
})

// Request interceptor: attach JWT token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: auto-refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // Don't retry if already refreshing or already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const success = await useAuthStore.getState().refreshJwtToken()
      if (success) {
        // Retry original request with new token
        const newToken = useAuthStore.getState().accessToken
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axios(originalRequest)
        }
      }
      
      // Refresh failed — redirect to login
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

// ── React Hook ─────────────────────────────────────────────────────────────
// Returns the axios instance + auth state
// ───────────────────────────────────────────────────────────────────────────

export function useApi() {
  const { accessToken, firebaseUser, isLoading, error, clearError } = useAuthStore()
  
  // Sync token from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('access_token')
    if (stored && !accessToken) {
      useAuthStore.setState({ accessToken: stored })
    }
  }, [accessToken])
  
  return {
    api,
    token: accessToken,
    user: firebaseUser,
    isLoading,
    error,
    clearError,
  }
}